import { useEffect, useState } from "react";
import { Badge, Button, Card, Modal, Spinner, Table } from "@themesberg/react-bootstrap";
import moment from "moment";
import { useCookies } from "react-cookie";
import { getTicketResultDetail } from "../../../../controller/api/__loto__/cart";
import { getLotoTypeLabelWithFilter } from "../../../../helper/lotoType";

const REGION_LABEL = {
  MB: "Miền Bắc",
  MT: "Miền Trung",
  MN: "Miền Nam",
};

const MB_PRIZE_NAMES = [
  "Giải ĐB",
  "Giải Nhất",
  "Giải Nhì",
  "Giải Ba",
  "Giải Tư",
  "Giải Năm",
  "Giải Sáu",
  "Giải Bảy",
];

const MNT_PRIZE_NAMES = [
  "Giải ĐB",
  "Giải Nhất",
  "Giải Nhì",
  "Giải Ba",
  "Giải Tư",
  "Giải Năm",
  "Giải Sáu",
  "Giải Bảy",
  "Giải Tám",
];

const formatVnd = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("vi-VN") + "đ";
};

export const getRewardValuePerHit = (ticket) => {
  const rewardCount = Number(ticket && ticket.rewardCount);
  const totalReward = Number(ticket && ticket.rewardValue);

  if (!Number.isFinite(rewardCount) || rewardCount <= 0 || !Number.isFinite(totalReward)) {
    return null;
  }

  return totalReward / rewardCount;
};

// Determine the comparison length(s) used for the given LotoType.
// Returns array of digit-lengths to compare against the LAST digits of each prize number.
const getCompareLengths = (lotoType, ticketNumbersLen) => {
  switch (lotoType) {
    case "MB_2":
    case "MB_BAO2":
    case "D":
    case "DAU2":
    case "C":
    case "CUOI2":
    case "DC":
    case "DAU_CUOI2":
    case "LO18":
    case "L18":
    case "BAO2":
      return [2];
    case "DAU3":
    case "CUOI3":
    case "DAU_CUOI3":
    case "L17":
      return [3];
    case "L4":
    case "L16":
      return [4];
    case "MB_3":
    case "L7":
      // Loto 3 số compares 3 digits to DB/Nhat/Sau, and last-2 of ticket to DB
      return [3, 2];
    case "MB_5":
    case "L5":
      // L5 compares varying lengths (5,4,3,2). Just compare by all suffix slices up to numbersLength
      return [ticketNumbersLen, 4, 3, 2];
    default:
      return [ticketNumbersLen];
  }
};

// Check if a prize number ends with any of the candidate suffixes derived from ticketNumbers.
const isPrizeMatched = (prizeNumber, ticketNumbers, compareLengths) => {
  if (!prizeNumber || !ticketNumbers) return false;
  for (const len of compareLengths) {
    if (len <= 0 || len > ticketNumbers.length) continue;
    const suffix = ticketNumbers.slice(-len);
    if (prizeNumber.length >= len && prizeNumber.slice(-len) === suffix) {
      return true;
    }
  }
  return false;
};

const renderPrizeNumber = (num, matched) => {
  return (
    <span
      key={num + Math.random()}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        margin: 2,
        borderRadius: 4,
        fontWeight: matched ? 700 : 400,
        backgroundColor: matched ? "#dc3545" : "#f1f3f5",
        color: matched ? "#fff" : "#212529",
        letterSpacing: 1,
      }}
    >
      {num}
    </span>
  );
};

export default function TicketResultDetailModal({ show, onHide, ticketId }) {
  const [cookies] = useCookies(["lottmekong"]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!show || !ticketId) {
      setData(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getTicketResultDetail(cookies.lottmekong, ticketId)
      .then((res) => {
        if (cancelled) return;
        setData(res || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, ticketId, cookies.lottmekong]);

  const ticket = data && data.ticket;
  const classicResult = data && data.classicResult;
  const dienToanResult = data && data.dienToanResult;
  const rewardValuePerHit = getRewardValuePerHit(ticket);

  const region = ticket && ticket.channel ? ticket.channel.region : null;
  const prizeNames = region === "MB" ? MB_PRIZE_NAMES : MNT_PRIZE_NAMES;

  const ticketNumbers = ticket ? ticket.numbers || "" : "";
  const compareLengths = ticket
    ? getCompareLengths(ticket.LotoType, ticketNumbers.length)
    : [];

  const renderStatusBadge = () => {
    if (!ticket) return null;
    if (ticket.status === 4) {
      const isPaid = ticket.rewardPaymentStatus === "paid";
      return (
        <>
          <Badge bg="danger" className="me-1">Trúng thưởng</Badge>
          <Badge bg={isPaid ? "success" : "warning"} text={isPaid ? undefined : "dark"}>
            {isPaid ? "Đã trả thưởng" : "Chờ trả thưởng"}
          </Badge>
        </>
      );
    }
    if (ticket.status === 5) return <Badge bg="secondary">Không trúng</Badge>;
    if (ticket.status === 3) return <Badge bg="primary">Chờ KQ</Badge>;
    return <Badge bg="warning" text="dark">Đang xử lý</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Chi tiết vé {ticketId ? `#${ticketId}` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && !data && (
          <div className="text-center text-muted py-4">Không có dữ liệu</div>
        )}

        {!loading && ticket && (
          <>
            <Card border="light" className="shadow-sm mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                <span className="fw-bold">Thông tin vé</span>
                {renderStatusBadge()}
              </Card.Header>
              <Card.Body>
                <Table responsive size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: 140 }}>Mã vé</td>
                      <td className="fw-bold text-primary">{ticket.id}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Cách chơi</td>
                      <td className="fw-bold">
                        {getLotoTypeLabelWithFilter(ticket.LotoType, ticket.baoFilter, ticket.baoDigits)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Số chơi</td>
                      <td className="fw-bold text-primary" style={{ letterSpacing: "2px" }}>
                        {(ticket.numbers || "").split("").join(" ")}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Giá vé</td>
                      <td className="fw-bold text-success">{formatVnd(ticket.value)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Kỳ quay</td>
                      <td>
                        {ticket.channel ? ticket.channel.name : ""}
                        {region && <span className="ms-2 text-muted">({REGION_LABEL[region] || region})</span>}
                        {ticket.resultDate && <span className="text-primary ms-2">- {ticket.resultDate}</span>}
                      </td>
                    </tr>
                    {ticket.dialDate && (
                      <tr>
                        <td className="text-muted">Ngày quay</td>
                        <td>{moment(ticket.dialDate).format("DD/MM/YYYY HH:mm")}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {ticket.status === 4 && (
              <Card border="light" className="shadow-sm mb-3">
                <Card.Header className="bg-light fw-bold text-danger">Kết quả trúng thưởng</Card.Header>
                <Card.Body>
                  <Table responsive size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: 160 }}>Loại trúng</td>
                        <td className="fw-bold">
                          {getLotoTypeLabelWithFilter(ticket.LotoType, ticket.baoFilter, ticket.baoDigits)}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Số lần trúng</td>
                        <td className="fw-bold">{Number(ticket.rewardCount || 0)}</td>
                      </tr>
                      {rewardValuePerHit !== null && (
                        <tr>
                          <td className="text-muted">Thưởng mỗi lần trúng</td>
                          <td className="fw-bold">
                            {formatVnd(rewardValuePerHit)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted">Tổng tiền thưởng</td>
                        <td className="fw-bold text-danger fs-5">{formatVnd(ticket.rewardValue)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {classicResult && classicResult.result && classicResult.result.length > 0 && (
              <Card border="light" className="shadow-sm mb-3">
                <Card.Header className="bg-light fw-bold">
                  Kết quả kỳ quay
                  <span className="text-muted ms-2">
                    (đối chiếu {compareLengths.length ? compareLengths.join("/") : ticketNumbers.length} số cuối)
                  </span>
                </Card.Header>
                <Card.Body>
                  <Table responsive size="sm" className="mb-0">
                    <tbody>
                      {classicResult.result.map((line, idx) => {
                        const nums = (line || "").split(" ").filter(Boolean);
                        return (
                          <tr key={`prize-${idx}`}>
                            <td className="text-muted" style={{ width: 110, verticalAlign: "middle" }}>
                              {prizeNames[idx] || `Giải ${idx}`}
                            </td>
                            <td>
                              {nums.length === 0 ? (
                                <span className="text-muted">-</span>
                              ) : (
                                nums.map((n) => renderPrizeNumber(n, isPrizeMatched(n, ticketNumbers, compareLengths)))
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <div className="text-muted small mt-2">
                    Số được tô đỏ là số có đoạn cuối trùng với số chơi của vé.
                  </div>
                </Card.Body>
              </Card>
            )}

            {dienToanResult && (
              <Card border="light" className="shadow-sm mb-3">
                <Card.Header className="bg-light fw-bold">
                  Kết quả {dienToanResult.lotteryName || "Điện toán"}
                  {dienToanResult.periodCode && <span className="text-muted ms-2">- Kỳ {dienToanResult.periodCode}</span>}
                </Card.Header>
                <Card.Body>
                  {dienToanResult.prizeDetails && dienToanResult.prizeDetails.length > 0 ? (
                    <Table responsive size="sm" className="mb-0">
                      <tbody>
                        {dienToanResult.prizeDetails.map((p, idx) => (
                          <tr key={`dt-${idx}`}>
                            <td className="text-muted" style={{ width: 160 }}>{p.prizeName || p.prizeCode || `Giải ${idx + 1}`}</td>
                            <td className="fw-bold">{p.prizeValue || p.value || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted">Chưa có chi tiết giải.</div>
                  )}
                </Card.Body>
              </Card>
            )}

            {ticket.images && ticket.images.length > 0 && (
              <Card border="light" className="shadow-sm">
                <Card.Header className="bg-light fw-bold">Hình ảnh vé</Card.Header>
                <Card.Body>
                  <div className="d-flex gap-3 flex-wrap justify-content-center">
                    {ticket.images.map((src, i) => src ? (
                      <img
                        key={i}
                        alt={`Ảnh ${i + 1}`}
                        src={src}
                        style={{ maxWidth: 250, maxHeight: 300, objectFit: "contain", borderRadius: 4 }}
                      />
                    ) : null)}
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
}
